import * as React from 'react'
import styled, { css } from 'styled-components'
import { Colors, Fonts } from '../constants'
import memoize from '../memoize'
import { DownArrow } from './core'

/*
Example Usage:

   const items = [
    {
      name: 'ivan',
      id: 0,
    },
    {
      name: 'grivan',
      id: 1,
    },
    {
      name: 'shmivan',
      id: 2,
    },
  ]
  const [selectedItemId, setSelectedItemId] = useState<number | undefined>(items[0].id)
  function onItemChange(item: ListItem | undefined) {
    setSelectedItemId(item && item.id)
  }
  return (
    <Dropdown
      items={items}
      selectedItemId={selectedItemId}
      onSelectedItemChange={onItemChange}
      placeholder={'placeholder'}
      disabled={false}
    />
  )
 */
interface DropdownProps<IdType extends number | string, ListItemT extends ListItem<IdType>> {
  /**
   * All the items that the user can choose between
   */
  items: ListItemT[]

  /**
   * Which item to show as selected. If undefined, no item is selected.
   */
  selectedItemId: IdType | undefined

  /**
   * Callback for listening to item changes.
   */
  onSelectedItemChange: (item: ListItemT) => void

  /**
   * Placeholder text for the typeahead input element. Defaults
   * to `Dropdown.DEFAULT_PLACEHOLDER_TEXT` if not set.
   */
  placeholder?: string
  disabled?: boolean
  width?: number
}

interface DropdownState<IdType extends number | string, ListItemT extends ListItem<IdType>> {
  inputText: string
  isOpen: boolean
  /**
   * This is the list of items that is _literally_ being shown to the user.
   * Filtering is based on the input that the user typed into the typeahead
   * input element, and is done by the `Dropdown.filterItemsBasedOnInput` function.
   */
  filteredItems: ListItemT[]
  hoveringIndex: number | null
  hasError: boolean
}

export interface ListItem<IdType extends number | string> {
  disabled?: boolean
  name: string
  id: IdType
}

/**
 * Component that implements a dropdown similar to the native <select> element,
 * allowing the user to choose among options. In addition, this component has
 * typeahead, and allows selection using the keyboard with the arrow keys.
 */
export class Dropdown<IdType extends number | string, ListItemT extends ListItem<IdType>> extends React.Component<
  DropdownProps<IdType, ListItemT>,
  DropdownState<IdType, ListItemT>
> {
  private static readonly DEFAULT_PLACEHOLDER_TEXT = 'Please choose …'

  private listItemHandlers = {}
  private ref: React.RefObject<HTMLDivElement>

  constructor(props: DropdownProps<IdType, ListItemT>) {
    super(props)

    this.state = {
      inputText: '',
      isOpen: false,
      filteredItems: this.props.items,
      hoveringIndex: null,
      hasError: false,
    }

    this.validateInputs()

    this.ref = React.createRef()
  }

  componentDidMount() {
    const selectedItem = this.currentlySelectedItem()

    if (selectedItem) {
      this.setState({
        inputText: selectedItem.name,
      })
    }

    document.addEventListener('mousedown', this.handleClickDocument)
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickDocument)
  }

  public componentDidUpdate() {
    this.validateInputs()
  }

  public render() {
    return (
      <Container onMouseLeave={this.onMouseLeave} width={this.props.width || 300} ref={this.ref}>
        <InputContainer>
          <DropdownInput
            disabled={this.props.disabled}
            hasError={this.state.hasError}
            onKeyUp={this.onKeyUp}
            onKeyDown={this.onKeyDown}
            onMouseDown={this.onInputMouseDown}
            value={this.state.inputText}
            onChange={this.onInputChange}
            placeholder={this.props.placeholder || Dropdown.DEFAULT_PLACEHOLDER_TEXT}
            roundBottomCorners={this.state.isOpen && this.state.filteredItems.length > 0}
          />
          <ArrowContainer>
            <DownArrow isDown={this.state.isOpen} />
          </ArrowContainer>
        </InputContainer>

        {this.state.isOpen && this.state.filteredItems.length > 0 ? (
          <DropdownContainerContainer>
            <DropdownListItemsContainer>
              {this.state.filteredItems.map((item: ListItemT, i: number) => (
                <ListItemContainer
                  key={item.id}
                  onMouseDown={memoize(this.listItemHandlers, `d-${item.id}`, () => () => this.onItemMouseDown(item))}
                  onMouseEnter={memoize(this.listItemHandlers, `e-${i}`, () => () => this.onItemHover(i))}
                  disabled={item.disabled}
                  hovering={i === this.state.hoveringIndex}
                  somethingElseHovering={i !== this.state.hoveringIndex && this.state.hoveringIndex !== null}
                  selected={item.id === this.props.selectedItemId}
                >
                  {item.name}
                </ListItemContainer>
              ))}
            </DropdownListItemsContainer>
          </DropdownContainerContainer>
        ) : null}
      </Container>
    )
  }

  // event handlers //

  private handleClickDocument = (e: MouseEvent) => {
    if (e.target instanceof Node && !this.ref.current?.contains(e.target)) {
      this.handleClickOutside()
    }
  }

  private handleClickOutside = () => {
    if (this.props.disabled) {
      return
    }

    this.cancelInteraction()
  }

  private onMouseLeave = () => {
    if (this.props.disabled) {
      return
    }

    this.setState({
      hoveringIndex: null,
    })
  }

  private onKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (this.props.disabled) {
      return
    }

    if (e.key === 'Enter') {
      if (this.state.hoveringIndex === null || this.state.hoveringIndex >= this.state.filteredItems.length) {
        this.setState({
          hasError: true,
        })
      } else {
        const selectedItem = this.state.filteredItems[this.state.hoveringIndex]

        if (selectedItem && this.state.isOpen) {
          this.selectItem(selectedItem)
        }
      }
    }
  }

  private onItemMouseDown = (item: ListItemT) => {
    if (this.props.disabled) {
      return
    }

    this.selectItem(item)
  }

  private onInputMouseDown = () => {
    if (this.props.disabled) {
      return
    }

    if (this.state.isOpen) {
      this.closeDropdown()
    } else {
      this.openDowndown()
    }
  }

  private onItemHover = (itemIndex: number) => {
    if (this.props.disabled) {
      return
    }

    this.makeHover(itemIndex)
  }

  private onInputChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    if (this.props.disabled) {
      return
    }

    this.setState({
      inputText: evt.target.value,
      isOpen: true,
      filteredItems: Dropdown.filterItemsBasedOnInput(evt.target.value, this.props.items),
      hoveringIndex: 0,
    })
  }

  // state management //

  private onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const allowedKeys = ['ArrowDown', 'ArrowUp', 'Escape']

    if (!allowedKeys.includes(e.key)) {
      return
    }

    if (e.key === 'Escape') {
      this.cancelInteraction()
      return
    }

    if (this.state.filteredItems.length === 0) {
      return
    }

    let currentIndex = this.state.hoveringIndex

    if (currentIndex === null || !this.state.isOpen) {
      currentIndex = 0
    } else {
      let delta = 0

      if (e.key === 'ArrowDown') {
        delta = 1
      } else if (e.key === 'ArrowUp') {
        delta = -1
      }

      currentIndex = (currentIndex + delta + this.state.filteredItems.length) % this.state.filteredItems.length
    }

    this.makeHover(currentIndex)
    this.openDowndown(true)
  }

  private validateInputs() {
    const existingIds: { [index in number | string]: boolean | undefined } = {}

    for (const item of this.props.items) {
      if (existingIds[item.id]) {
        console.warn(`duplicate dropdown list item id: ${item.id} ${JSON.stringify(item)}`)
      } else {
        existingIds[item.id] = true
      }
    }

    if (this.props.selectedItemId !== undefined && !existingIds[this.props.selectedItemId]) {
      console.warn(`selected item id ${this.props.selectedItemId} does not correspond to an existing list item`)
    }
  }

  private closeDropdown() {
    if (this.state.isOpen) {
      this.setState({
        isOpen: false,
        hoveringIndex: null,
      })
    }
  }

  private openDowndown(hoverOverFirstElement = false) {
    if (this.state.filteredItems.length !== 0 && !this.state.isOpen) {
      this.setState({
        isOpen: true,
        hoveringIndex: hoverOverFirstElement ? 0 : null,
        filteredItems: this.props.items,
      })
    }
  }

  private makeHover(index: number) {
    if (index < this.state.filteredItems.length && index >= 0) {
      this.setState({
        hoveringIndex: index,
      })
    }
  }

  private currentlySelectedItem(): ListItemT | null {
    return this.props.items.find(item => this.props.selectedItemId === item.id) || null
  }

  private cancelInteraction() {
    const previouslySelectedItem = this.currentlySelectedItem()

    if (previouslySelectedItem) {
      this.selectItem(previouslySelectedItem)
    }

    this.closeDropdown()
  }

  private selectItem(item: ListItemT) {
    if (!item.disabled) {
      if (item.id !== this.props.selectedItemId) {
        this.props.onSelectedItemChange(item)
      }

      this.setState({
        isOpen: false,
        inputText: item.name,
        hasError: false,
        filteredItems: Dropdown.filterItemsBasedOnInput(item.name, this.props.items),
      })
    }
  }

  private static filterItemsBasedOnInput<IdType extends number | string, ListItemT extends ListItem<IdType>>(
    inputText: string,
    items: ListItemT[]
  ): ListItemT[] {
    if (inputText === null || inputText.length === 0) {
      return items
    }

    return items.filter(item => item.name.toLowerCase().includes(inputText.toLowerCase()))
  }
}

const DropdownInput = styled.input`
  ${({ hasError, roundBottomCorners }: { hasError: boolean; roundBottomCorners: boolean }) =>
    css`
      padding: 8px;
      border: ${hasError ? `1px solid ${Colors.coral}` : 'solid 1px #2f2b30'};
      background-color: ${Colors.ink};
      color: white;
      width: 100%;
      border-radius: 4px 4px ${roundBottomCorners ? '0 0' : '4px 4px'};
      font-size: 14px;
      box-sizing: border-box;
      cursor: pointer;
    `}
`

const ListItemContainer = styled.div`
  ${({
    selected,
    hovering,
    somethingElseHovering,
    disabled,
  }: {
    selected: boolean
    hovering: boolean
    somethingElseHovering: boolean
    disabled?: boolean
  }) => {
    const hoverStyle = css`
      color: white;
      background-color: ${Colors.steel};
    `

    const defaultStyle = css`
      color: white;
      background-color: ${Colors.ink};
    `

    const activeStyle = css`
      color: ${Colors.sky};
      background-color: ${Colors.ink};
    `

    const hoverActiveStyle = css`
      color: ${Colors.sky};
      background-color: ${Colors.steel};
    `

    const somethingElseHoveredOverStyle = css`
      background-color: ${Colors.ink};
    `

    const disabledStyle = css`
      color: ${Colors.steel};
      background-color: ${Colors.ink};
    `

    const disabledHoverStyle = css`
      color: ${Colors.ink};
      background-color: ${Colors.steel};
    `

    return css`
      transition: all 0.05s linear 0s;
      font-size: 14px;
      border-radius: 2px;
      font-family: ${Fonts.sansHeader};
      box-sizing: border-box;
      padding: 8px;
      width: 100%;
      background-color: ${selected ? 'green' : 'white'};
      cursor: pointer;
      ${defaultStyle}
      ${hovering ? hoverStyle : ''}
      ${somethingElseHovering ? somethingElseHoveredOverStyle : ''}
      ${selected ? activeStyle : ''}
      ${hovering && selected ? hoverActiveStyle : ''}
      ${disabled ? disabledStyle : ''}
      ${disabled && hovering ? disabledHoverStyle : ''}
    `
  }}
`

const Container = styled.div`
  ${({ width }: { width: number }) =>
    css`
      display: inline-block;
      border-radius: 2px;
      width: ${width}px;
    `}
`

const DropdownContainerContainer = styled.div`
  position: relative;
  width: 100%;
`

const DropdownListItemsContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  padding: 4px;
  box-sizing: border-box;
  border: solid 1px #2f2b30;
  border-top: none;
  background-color: ${Colors.ink};
  max-height: 250px;
  overflow-y: scroll;
  z-index: 1000;
`

const InputContainer = styled.div`
  position: relative;
  width: 100%;
`

const ArrowContainer = styled.div`
  position: absolute;
  right: 8px;
  top: 0;
  width: 10px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`
